// ════════════════════════════════════════════════════════════════════════
// RTR SCORE — RÈGLES CLINIQUES STRUCTURÉES
// Source de vérité unique pour le moteur de scoring (src/rtrEngine.js).
// Source de vérité clinique : docs/regles/campus_lca_regles_cliniques_v1_15.yaml
// (fichier unique V1.15 : V1.13 + delta V1.14 + journal daté ; anciens fichiers dans
// docs/regles/archive/). Section RTR = V1.13 §5 ; alignements V1.14 du 22/10/2026
// (dérobement post-op bloquant, question obligatoire au bilan, G1–G9, consigne Joris) ;
// décisions J. Tubez du 23/10/2026 (H4 flexion ≥ 125°, H8 bassin neutre non bloquant).
//
// Règles de calcul (§5.1, sourcées telles quelles) :
//   R1 — Hurdles d'abord. Un seul BLOQUANT en échec → NO_GO, score non calculé.
//   R2 — NEUTRALISÉE (23/07/2026, U7 tranché). Un bloc faible ne plafonne plus
//        la transition. La décision GO/GO_CONDITIONNEL/NO_GO repose EXCLUSIVEMENT
//        sur R1 (hurdles). Un bloc faible reste visible/signalé, jamais décisionnel.
//        (Le §5.3 du document source contient encore une phrase résiduelle
//        "Rappel R2 : tout bloc < 60% plafonne à GO_CONDITIONNEL" — non appliquée
//        ici : elle contredit la neutralisation actée en §5.1/§7.1/journal V1.11
//        et le mandat explicite de cette session. Signalé à Jérôme, pas codé.)
//   R3 — Un critère NON_MESURE est retiré du numérateur ET du dénominateur du
//        score affiché (renormalisation), jamais compté comme 0.
//   R4 — Condition conjointe sur mesures symétriques : valeur absolue OU %EPIC
//        ET LSI (pas de LSI seul).
//   R5 — Pondérations = EXCELLENCE, non bloquant. Le score /100 est un indicateur
//        de suivi, jamais un critère de décision RTS.
//
// ── Conventions mécaniques documentées (PAS des seuils cliniques) ──────────
// Le document V1.13 sourcés très précisément les HURDLES (déterminants de la
// décision) et les poids de BLOC. Il ne détaille pas toujours la répartition
// des points *à l'intérieur* d'un bloc, ni le barème 0→max de chaque variable
// individuelle. Comme le score est non-décisionnel (R5), deux conventions
// mécaniques sont appliquées et documentées champ par champ ci-dessous :
//   - `scale: { mode:"linear_range", min, max }` → barème donné explicitement
//     dans le texte V1.13 (ex. "mollet 25=0pt, 35=max"). V1.15 (D2, J. Tubez 23/10/2026) :
//     atteindre `min` (le seuil) vaut 50 % des points, `max` 100 % ; sous le seuil, crédit
//     proportionnel (valeur/seuil × 50 %). Motif : un critère mesuré au seuil ne doit plus
//     valoir moins qu'un critère non mesuré (retiré du calcul, R3).
//   - `scale: { mode:"ratio_to_threshold" }` → pas de plafond donné : la
//     fraction du bloc est value/threshold plafonnée à 1 (mécanique, pas clinique).
//   - `scale: { mode:"direct_percent" }` → la variable est déjà un pourcentage
//     0-100 sans barème custom donné (ex. adherence_pct).
//   - `scale: { mode:"boolean" }` → toggle oui/non, tout ou rien.
//   - `scale: { mode:"categorical", map:{...} }` → échelle qualitative (Crossley…).
//   - Poids de bloc sans sous-répartition documentée en V1.13 → répartition
//     égale entre les critères du bloc, marquée `weightSource:"repartition_egale"`.
// ════════════════════════════════════════════════════════════════════════

export const RTR_VERSION = "V1.15";
export const RTR_PROFILES = ["COMPETITIF", "INTERMEDIAIRE", "RECREATIF"];
export const RTR_PROFILE_LABELS = {
  COMPETITIF: "Compétitif",
  INTERMEDIAIRE: "Intermédiaire",
  RECREATIF: "Récréatif",
};

// ────────────────────────────────────────────────────────────────────────
// RTR-RUN — Autorisation de course (gate T4, P2 → P3). Score unique, ne
// différencie pas les profils (§5.2, validé 16/07/2026).
// ────────────────────────────────────────────────────────────────────────
export const RTR_RUN = {
  id: "RTR-RUN",
  label: "RTR-RUN — Autorisation de course",
  sourceRef: "§5.2, gate T4 (P2→P3)",
  profileDependent: false,

  hurdles: [
    { id: "delai_postop", label: "Délai post-op", unit: "sem", direction: "gte", threshold: 12, sourceRef: "H1" },
    { id: "epanchement_drills", label: "Épanchement", direction: "lte_qualitative", threshold: "trace", sourceRef: "H2" },
    { id: "extension", label: "Extension", direction: "eq", threshold: "complète (0°)", sourceRef: "H3" },
    { id: "flexion", label: "Flexion passive (côté opéré)", unit: "°", direction: "gte", threshold: 125, sourceRef: "H4", note: "V1.15 (J. Tubez, 23/10/2026) : bloquant ≥ 125° ; 135° reste la cible Aspetar." },
    { id: "douleur_course", label: "Douleur pendant les drills de course", unit: "/10", direction: "eq", threshold: 0, sourceRef: "H5" },
    { id: "reaction_j1", label: "Réaction J+1 — douleur ou gonflement le lendemain de la dernière séance de drills de course", direction: "eq_bool", threshold: false, ref: "Aucune réaction (réponse attendue : non)", sourceRef: "H6" },
    { id: "pogos_sl", label: "Pogos unipodaux forme correcte", unit: "reps", direction: "gte", threshold: 30, sourceRef: "H7" },
    {
      id: "quad_lsi", label: "LSI quadriceps", unit: "%", direction: "gte", threshold: 70,
      ref: "Bloquant ≥ 70 % — on vise 80 % (prévention de la récidive, règle 60/70/80)",
      note: "Checkpoint isocinétique T4. Sous 80 % : course autorisée, mais le renforcement quadriceps reste l'objectif prioritaire.",
      fallback: { label: "Fallback MicroFET", condition: "quad_lsi_microfet ≥ 70% ET quad_epic ≥ 70%", capStatus: "GO_CONDITIONNEL" },
      sourceRef: "H9",
    },
    { id: "calf_raise_reps", label: "Élévations mollet unipodales (absolu, côté opéré)", unit: "reps", direction: "gte", threshold: 25, sourceRef: "H9b", flag: "⭐ muscle qui travaille le plus dur aux vitesses de course initiales" },
    { id: "olr_reps", label: "Relevé unipodal (OLR, absolu, côté opéré)", unit: "reps", direction: "gte", threshold: 10, sourceRef: "H9c" },
    { id: "ikdc", label: "IKDC", unit: "pts", direction: "gt", threshold: 64, sourceRef: "H10" },
    { id: "sl_squat_quality", label: "Qualité squat unipodal (Crossley)", direction: "gte_categorical", threshold: "bon", sourceRef: "H11" },
    { id: "epanchement_p2_exit", label: "Épanchement (Melbourne, sortie P2)", direction: "eq", threshold: 0, sourceRef: "H12" },
    {
      id: "derobements", label: "Dérobement post-op (0 toléré)", direction: "zero_giving_way", threshold: 0,
      sourceRef: "V1.14 regle_giving_way.post_op",
      note: "Question « Dérobement depuis le dernier bilan ? » obligatoire. Sans réponse → À RETESTER, jamais PASS.",
    },
  ],

  // Informatif — affiché et saisi dans le gate, jamais bloquant, hors score.
  informative: [
    { id: "bassin_neutre", label: "Bassin neutre dans le plan frontal en midstance (Runeasi / vidéo)", direction: "eq_bool", threshold: true, sourceRef: "ex-H8",
      note: "V1.15 (J. Tubez, 23/10/2026) : retiré des hurdles — informatif, n'influence ni la décision ni le score." },
    { id: "dj_rsi_lsi", label: "RSI drop jump — LSI (optionnel)", unit: "%", direction: "info", ref: "Optionnel : RSI côté opéré / côté sain × 100", sourceRef: "D6",
      note: "V1.15 (D6) : le score utilise le RSI absolu côté opéré (m/s) ; le LSI reste consultable." },
  ],

  blocks: [
    {
      id: "B1", label: "Force (hiérarchie de course)", weight: 30,
      note: "Hiérarchie de tâche à ces vitesses : mollet > quad > ischios/fessiers/fléchisseurs hanche (Hughes). Corrigé en V1.5.",
      criteria: [
        { id: "calf_raise_reps", label: "Élévations mollet (absolu)", weight: 12, unit: "reps", scale: { mode: "linear_range", min: 25, max: 35 }, weightSource: "sourcé" },
        { id: "quad_isok_lsi", label: "Force quad — LSI isocinétique", weight: 9, unit: "%", scale: { mode: "linear_range", min: 70, max: 85 }, weightSource: "sourcé" },
        { id: "quad_epic", label: "Force quad — %EPIC", weight: 6, unit: "%", scale: { mode: "linear_range", min: 70, max: 85 }, weightSource: "sourcé (poids) / barème = même plage que LSI (non spécifiée séparément en V1.13)" },
        { id: "ham_isok_lsi", label: "Force ischio-jambiers — LSI", weight: 3, unit: "%", scale: { mode: "linear_range", min: 70, max: 85 }, weightSource: "sourcé (poids) / barème = convention quad (seuil 70% sourcé Melbourne 2.0, cf. CRITERIA.ij_iso)" },
      ],
    },
    {
      id: "B2", label: "Force réactive", weight: 25,
      note: "Évaluation à l'œil acceptée (Hughes).",
      criteria: [
        { id: "pogo_sl_reps", label: "Pogos unipodaux", weight: 6, unit: "reps", scale: { mode: "ratio_to_threshold", threshold: 30 }, weightSource: "sourcé" },
        { id: "hop_vh_tolere", label: "Hopping vertical + horizontal toléré", weight: 6, scale: { mode: "boolean" }, weightSource: "sourcé" },
        { id: "cone_hop_fwd_bwd", label: "Cone hop avant/arrière", weight: 4, scale: { mode: "boolean" }, weightSource: "sourcé" },
        { id: "tuck_jump_sl", label: "Tuck jump unipodal", weight: 4, scale: { mode: "boolean" }, weightSource: "sourcé" },
        { id: "dj_rsi_sl", label: "RSI drop jump unipodal — côté opéré", weight: 5, unit: "m/s", scale: { mode: "ratio_to_threshold", threshold: 0.4 }, weightSource: "sourcé (poids) / réf. 0,4 m/s = RSI SL récréatif §5.3 ; V1.15 (D6) : mesure absolue côté opéré (My Jump Lab), plus un LSI" },
      ],
    },
    {
      id: "B3", label: "Contrôle & capacité unipodale", weight: 20,
      criteria: [
        { id: "sl_squat_quality", label: "Single Leg Squat (Crossley)", weight: 7, scale: { mode: "categorical", map: { "Pauvre (Poor)": 0, "Passable (Fair)": 0.5, "Bon (Good)": 1 } }, weightSource: "sourcé" },
        { id: "olr_reps", label: "OLR (absolu)", weight: 6, unit: "reps", scale: { mode: "linear_range", min: 10, max: 15 }, weightSource: "sourcé" },
        { id: "sl_bridge", label: "Single Leg Bridge", weight: 3, unit: "reps", scale: { mode: "ratio_to_threshold", threshold: 20 }, weightSource: "sourcé (poids) / seuil Melbourne 2.0 p.11 (CRITERIA.sl_bridge)" },
        { id: "balance", label: "Balance vestibulaire", weight: 2, scale: { mode: "boolean" }, weightSource: "sourcé" },
        { id: "side_bridge_time", label: "Side Bridge Endurance", weight: 2, unit: "sec", scale: { mode: "ratio_to_threshold", threshold: 30 }, weightSource: "sourcé (poids) / seuil Melbourne 2.0 p.11 (CRITERIA.side_bridge)" },
      ],
    },
    {
      id: "B4", label: "Tolérance de charge", weight: 15,
      note: "V1.14 : le dérobement post-op est désormais un HURDLE bloquant (cf. hurdle « derobements »). Le critère giving_way reste ici pour le score de suivi uniquement (poids V1.13 conservé, non re-pondéré).",
      criteria: [
        { id: "pain_next_morning_14j", label: "Aucune douleur au réveil sur les 14 j avant le bilan", weight: 6, scale: { mode: "boolean" }, ref: "Oui = aucune douleur au réveil sur les 14 jours précédant le bilan", weightSource: "sourcé ; fenêtre précisée V1.15 (D3)" },
        { id: "rpe_session_stable", label: "RPE de séance stable (2 dernières semaines)", weight: 3, scale: { mode: "boolean" }, ref: "Oui = écart ≤ 1 point (CR10) entre séances des 14 derniers jours — calculé depuis le carnet de séances si ≥ 2 séances notées", weightSource: "sourcé ; définition V1.15 (D4), écart ≤ 1 point = convention à valider" },
        { id: "adherence_pct", label: "Adhérence au programme (4 dernières semaines)", weight: 3, unit: "%", scale: { mode: "direct_percent" }, ref: "% = séances notées au carnet sur 28 j ÷ (séances prescrites/sem × 4) ; 100 % = max", weightSource: "sourcé ; calcul V1.15 (D5)" },
        { id: "giving_way", label: "Absence de dérobement", weight: 3, scale: { mode: "boolean" }, weightSource: "sourcé" },
      ],
    },
    {
      id: "B5", label: "Psychologique", weight: 10,
      criteria: [
        { id: "acl_rsi", label: "ACL-RSI", weight: 7, unit: "/100", scale: { mode: "linear_range", min: 65, max: 80 }, weightSource: "sourcé" },
        { id: "tsk11", label: "TSK-11", weight: 3, scale: { mode: "linear_range_inverse", min: 18, max: 44 }, weightSource: "sourcé (poids) / bornes = seuil TSK-11≤18 (U3 tranché) et max théorique de l'échelle (11-44 standard)" },
      ],
    },
  ],

  decision: [
    { min: 80, status: "GO", action: "Entrée UDel niveau 1. Progression max 2 niveaux/semaine." },
    { min: 65, max: 79, status: "GO_CONDITIONNEL", action: "Course autorisée avec restriction nommée (volume plafonné, pas de progression de vitesse). Re-test ≤ 4 semaines." },
    { max: 64, status: "NO_GO", action: "Maintien P2. Le bloc le plus faible devient l'objectif prioritaire." },
  ],
};

// ────────────────────────────────────────────────────────────────────────
// RTR-SPORT — Autorisation de compétition (gate T6, P4 → P4-RTS). 3 profils.
// ────────────────────────────────────────────────────────────────────────

// Hurdles communs aux 3 profils (§5.3)
const SPORT_HURDLES_COMMON = [
  { id: "delai_postop", label: "Délai post-op", unit: "mois", direction: "gte", threshold: 9, sourceRef: "H1" },
  { id: "tsk11", label: "TSK-11", direction: "lte", threshold: 18, sourceRef: "H2", note: "Éliminatoire, arrête la batterie." },
  { id: "epanchement", label: "Épanchement", direction: "eq", threshold: 0, sourceRef: "H3" },
  { id: "rom", label: "Extension / flexion", direction: "eq", threshold: "complète / ≥ 130°", sourceRef: "H4" },
  { id: "mrss", label: "MRSS 2.0", direction: "gte", threshold: 95, sourceRef: "H5" },
  { id: "condition_generale", label: "Tests de condition physique généraux", direction: "gte_baseline", threshold: "≥ pré-blessure", sourceRef: "H6" },
  { id: "prevention", label: "Programme de prévention", direction: "eq_bool", threshold: true, note: "≥ 15 min avant chaque séance", sourceRef: "H7" },
  { id: "accord_chirurgien", label: "Accord chirurgien", direction: "eq_bool", threshold: true, note: "tracé par écrit", sourceRef: "H8" },
  { id: "derobements", label: "Dérobement post-op (0 toléré)", direction: "zero_giving_way", threshold: 0, sourceRef: "H9 · V1.14 regle_giving_way.post_op", note: "Question « Dérobement depuis le dernier bilan ? » obligatoire. Sans réponse → À RETESTER, jamais PASS." },
  { id: "symetrie_lsi", label: "Symétrie (LSI), toutes variables", unit: "%", direction: "gte", threshold: 90, sourceRef: "§5.3 note symétrie" },
];

// Hurdles additionnels — profil INTERMÉDIAIRE uniquement (catégories "EXIGÉ — BLOQUANT").
// Mêmes identifiants que les entrées SPORT_THRESHOLDS correspondantes (cod, conditioning,
// sports_specific) : une seule saisie sert à la fois au hurdle et au score, pas de doublon.
const SPORT_HURDLES_INTERMEDIAIRE = [
  { id: "cod", label: "COD 90° planifié et non planifié", direction: "gte_baseline", threshold: "exigé", sourceRef: "§5.3 tableau seuils — colonne Intermédiaire" },
  { id: "conditioning", label: "Yo-Yo ou test spécifique équivalent", direction: "gte_baseline", threshold: "exigé", sourceRef: "§5.3 tableau seuils — colonne Intermédiaire" },
  { id: "sports_specific", label: "Programme sport-spécifique complété", direction: "eq_bool", threshold: true, sourceRef: "§5.3 tableau seuils — colonne Intermédiaire" },
];

// Seuils absolus par profil — table §5.3 (18 lignes, tier de mesure inclus)
const SPORT_THRESHOLDS = {
  motor_control: {
    label: "Squat unipodal", composante: "motor_control", tier: "A", inputKind: "meets_bool",
    COMPETITIF: "> 90° flexion, contrôle frontal neutre", INTERMEDIAIRE: "idem", RECREATIF: "idem",
  },
  hip_abd: {
    label: "Abduction hanche — force excentrique de pointe", unit: "% PC", composante: "strength", tier: "B (dynamomètre manuel, isométrique)", inputKind: "symmetric",
    COMPETITIF: 40, INTERMEDIAIRE: 30, RECREATIF: 30,
  },
  hip_add: {
    label: "Adduction hanche — force excentrique de pointe", unit: "% PC", composante: "strength", tier: "B (dynamomètre manuel, isométrique)", inputKind: "symmetric",
    COMPETITIF: 40, INTERMEDIAIRE: 30, RECREATIF: 30,
  },
  ratio_add_abd: {
    label: "Ratio adducteurs/abducteurs (isométrique)", composante: "strength", tier: "EXCELLENCE — non bloquant", inputKind: "informative_number",
    COMPETITIF: "informatif", INTERMEDIAIRE: "informatif", RECREATIF: "informatif",
    excellenceOnly: true, note: "Non prédictif une fois les études poolées (Quintana-Cepedal 2025) — jamais utilisé pour la décision ni le score.",
  },
  hip_re: {
    label: "Rotation externe hanche — force isométrique de pointe", unit: "% PC", composante: "strength", tier: "A (dynamomètre manuel)", inputKind: "symmetric",
    COMPETITIF: 30, INTERMEDIAIRE: 25, RECREATIF: 25,
  },
  calf_mvc: {
    label: "Fléchisseurs plantaires — force isométrique de pointe", unit: "% PC", composante: "strength", tier: "A (dynamomètre manuel)", inputKind: "symmetric",
    COMPETITIF: 200, INTERMEDIAIRE: 150, RECREATIF: 150,
  },
  quad_isok: {
    label: "Quadriceps — couple concentrique isocinétique de pointe", unit: "% PC", composante: "strength", tier: "A (isocinétique partenaire)", inputKind: "symmetric",
    COMPETITIF: 300, INTERMEDIAIRE: 260, RECREATIF: 260,
  },
  ham_isok: {
    label: "Ischio-jambiers — couple concentrique isocinétique de pointe", unit: "% PC", composante: "strength", tier: "A (isocinétique partenaire)", inputKind: "symmetric",
    COMPETITIF: 175, INTERMEDIAIRE: 160, RECREATIF: 160,
  },
  cmj_impulsion: {
    label: "CMJ bipodal & unipodal — impulsion conc./exc.", composante: "explosiveness", tier: "NON_MESURE", inputKind: "non_mesure",
    COMPETITIF: "baseline/moyenne du sport", INTERMEDIAIRE: "baseline/moyenne du sport", RECREATIF: "baseline/moyenne du sport",
    nonMesure: true, note: "Plateforme de force absente — retiré du numérateur ET dénominateur (R3), jamais compté comme 0.",
  },
  cmj_height: {
    label: "CMJ — hauteur", composante: "explosiveness", tier: "A (My Jump 2)", inputKind: "meets_bool",
    COMPETITIF: "baseline/moyenne du sport", INTERMEDIAIRE: "baseline/moyenne du sport", RECREATIF: "baseline/moyenne du sport",
  },
  sl_hop_distance: {
    label: "Single leg hop for distance", composante: "explosiveness", tier: "A", inputKind: "meets_bool",
    COMPETITIF: "baseline/moyenne du sport", INTERMEDIAIRE: "exigé", RECREATIF: "non exigé",
  },
  drop_jump: {
    label: "Drop jump bipodal & unipodal", composante: "explosiveness", tier: "A partiel", inputKind: "meets_bool",
    COMPETITIF: "baseline/moyenne du sport", INTERMEDIAIRE: "idem", RECREATIF: "idem",
  },
  contact_time: {
    label: "Temps de contact", unit: "s", composante: "reactive_strength", tier: "A (My Jump 2)", inputKind: "dlsl",
    COMPETITIF: "DL < 0,3 · SL < 0,35", INTERMEDIAIRE: "DL < 0,3 · SL < 0,35", RECREATIF: "DL < 0,3 · SL < 0,35",
    structured: {
      COMPETITIF: { dl: 0.3, sl: 0.35, direction: "lte" },
      INTERMEDIAIRE: { dl: 0.3, sl: 0.35, direction: "lte" },
      RECREATIF: { dl: 0.3, sl: 0.35, direction: "lte" },
    },
  },
  rsi: {
    label: "RSI", composante: "reactive_strength", tier: "A (My Jump 2, FT/GCT)", inputKind: "dlsl",
    COMPETITIF: "DL > 1,5 · SL > 0,5", INTERMEDIAIRE: "DL > 1,3 · SL > 0,4", RECREATIF: "DL > 1,3 · SL > 0,4",
    structured: {
      COMPETITIF: { dl: 1.5, sl: 0.5, direction: "gte" },
      INTERMEDIAIRE: { dl: 1.3, sl: 0.4, direction: "gte" },
      RECREATIF: { dl: 1.3, sl: 0.4, direction: "gte" },
    },
  },
  running_biomeca: {
    label: "Tapis — biomécanique", unit: "km/h", composante: "running", tier: "A (Runeasi)", inputKind: "meets_bool",
    COMPETITIF: "16 · bassin/hanche neutre en midstance", INTERMEDIAIRE: "16 (si terrain) · idem", RECREATIF: "12–16 selon sport · idem",
  },
  cod: {
    label: "COD 90° planifié et non planifié", composante: "cod", tier: "B (proxy vidéo)", inputKind: "meets_bool",
    COMPETITIF: "baseline/moyenne du sport", INTERMEDIAIRE: "exigé — BLOQUANT", RECREATIF: "non exigé",
  },
  conditioning: {
    label: "Yo-Yo ou test spécifique équivalent", composante: "conditioning", tier: "A", inputKind: "meets_bool",
    COMPETITIF: "distance + récup FC ≥ baseline", INTERMEDIAIRE: "exigé — BLOQUANT", RECREATIF: "non exigé",
  },
  sports_specific: {
    label: "Programme sport-spécifique complété", composante: "sports_specific", tier: "A", inputKind: "meets_bool",
    COMPETITIF: "tous scénarios validés, drills fermés+ouverts, sans réaction", INTERMEDIAIRE: "exigé — BLOQUANT", RECREATIF: "non exigé",
  },
};

// Composantes affichées (8 nommées CLAUDE.md + 2 transverses clinique/psycho)
export const RTR_SPORT_COMPOSANTES = [
  { id: "clinical", label: "Clinique (ROM / épanchement)" },
  { id: "motor_control", label: "Motor Control" },
  { id: "strength", label: "Strength" },
  { id: "explosiveness", label: "Explosiveness" },
  { id: "reactive_strength", label: "Reactive Strength (RSI)" },
  { id: "running", label: "Running" },
  { id: "cod", label: "COD" },
  { id: "conditioning", label: "Conditioning" },
  { id: "sports_specific", label: "Sports Specific" },
  { id: "psychological", label: "Psychologique" },
];

// Pondérations /100 par bloc et par profil (§5.3, sous-répartitions internes
// aux blocs non détaillées en V1.13 → répartition égale documentée par critère,
// sauf B5 dont le split course/COD EST donné explicitement dans le texte).
const SPORT_BLOCKS = {
  COMPETITIF: [
    { id: "B1", label: "Clinique / ROM / épanchement", weight: 10, composantes: ["clinical"], autoFullIfHurdlesPass: true },
    { id: "B2", label: "Force isolée & symétrie", weight: 25, composantes: ["motor_control", "strength"] },
    { id: "B3", label: "Explosivité", weight: 20, composantes: ["explosiveness"] },
    { id: "B4", label: "Force réactive", weight: 15, composantes: ["reactive_strength"] },
    { id: "B5", label: "Course & COD", weight: 15, split: { running: 7, cod: 8 } },
    { id: "B6", label: "Psychologique", weight: 10, composantes: ["psychological"] },
    { id: "B7", label: "Charge sportive complétée", weight: 5, split: { conditioning: 2.5, sports_specific: 2.5 } },
  ],
  INTERMEDIAIRE: [
    { id: "B1", label: "Clinique / ROM / épanchement", weight: 10, composantes: ["clinical"], autoFullIfHurdlesPass: true },
    { id: "B2", label: "Force isolée & symétrie", weight: 25, composantes: ["motor_control", "strength"] },
    { id: "B3", label: "Explosivité", weight: 20, composantes: ["explosiveness"] },
    { id: "B4", label: "Force réactive", weight: 15, composantes: ["reactive_strength"] },
    { id: "B5", label: "Course & COD", weight: 15, split: { running: 7, cod: 8 } },
    { id: "B6", label: "Psychologique", weight: 10, composantes: ["psychological"] },
    { id: "B7", label: "Charge sportive complétée", weight: 5, split: { conditioning: 2.5, sports_specific: 2.5 } },
  ],
  RECREATIF: [
    { id: "B1", label: "Clinique / ROM / épanchement", weight: 10, composantes: ["clinical"], autoFullIfHurdlesPass: true },
    { id: "B2", label: "Force isolée & symétrie", weight: 30, composantes: ["motor_control", "strength"] },
    { id: "B3", label: "Explosivité", weight: 25, composantes: ["explosiveness"] },
    { id: "B4", label: "Force réactive", weight: 15, composantes: ["reactive_strength"] },
    { id: "B5", label: "Course (seule, pas de COD)", weight: 8, split: { running: 8 } },
    { id: "B6", label: "Psychologique", weight: 12, composantes: ["psychological"] },
    { id: "B7", label: "Charge sportive complétée", weight: 0, split: {} },
  ],
};

const SPORT_DECISION = {
  COMPETITIF: [
    { min: 90, status: "GO" },
    { min: 80, max: 89, status: "GO_CONDITIONNEL", action: "Entraînement complet autorisé, match interdit ; re-test 6 semaines." },
    { max: 79, status: "NO_GO" },
  ],
  INTERMEDIAIRE: [
    { min: 88, status: "GO" },
    { min: 78, max: 87, status: "GO_CONDITIONNEL", action: "Entraînement complet autorisé, match interdit ; re-test 6 semaines." },
    { max: 77, status: "NO_GO" },
  ],
  RECREATIF: [
    { min: 85, status: "GO" },
    { min: 75, max: 84, status: "GO_CONDITIONNEL", action: "Pratique restreinte nommée ; re-test 6 semaines." },
    { max: 74, status: "NO_GO" },
  ],
};

export const RTR_SPORT = {
  id: "RTR-SPORT",
  label: "RTR-SPORT — Autorisation de compétition",
  sourceRef: "§5.3, gate T6 (P4→P4-RTS)",
  profileDependent: true,
  hurdlesCommon: SPORT_HURDLES_COMMON,
  hurdlesByProfile: { COMPETITIF: [], INTERMEDIAIRE: SPORT_HURDLES_INTERMEDIAIRE, RECREATIF: [] },
  thresholds: SPORT_THRESHOLDS,
  blocksByProfile: SPORT_BLOCKS,
  decisionByProfile: SPORT_DECISION,
};

// ────────────────────────────────────────────────────────────────────────
// V1.14 — DÉROBEMENT (giving way). Source : campus_lca_v1_14_FUSION.yaml
// §B regle_giving_way + décisions J. Tubez du 22/10/2026.
// ────────────────────────────────────────────────────────────────────────
export const GIVING_WAY_RULES = {
  post_op: {
    threshold: 0, statut: "BLOQUANT", gates: ["T3 (P1→P2)", "T4 (RTR-RUN)", "T6 (RTR-SPORT)"],
    source: "Décision clinique J. Tubez (pas une citation de source)",
    // Convention clinique N4 (J. Tubez, 22/10/2026)
    fenetre: "depuis l'entrée dans la phase en cours (dernier GO ou GO_CONDITIONNEL de la transition qui y mène), sinon depuis la date d'opération (P1, ou aucune décision enregistrée)",
    comparaison: ">= : un épisode le jour même de la borne compte",
    niveau_preuve: "N4",
    question_obligatoire: "« Dérobement depuis le dernier bilan ? » oui/non à chaque bilan. Sans réponse du jour → À RETESTER, jamais PASS. « Oui » → échec.",
  },
  pre_op: {
    threshold: 1, statut: "INFORMATIF",
    usage: "classification coper (Fitzgerald 2000) — futur module /decision-operation, jamais le gating post-op",
    fenetre: "événements antérieurs à la date d'opération (pas de date de blessure en base : épisodes antérieurs au dossier possiblement manquants)",
    source: "[FIT2000] repris par M. Hughes ; confirmé J. Tubez 16/10/2026",
  },
};

// ────────────────────────────────────────────────────────────────────────
// V1.14 — SEUILS DE FORCE G1–G9 (registre de gel, dégelés le 16/10/2026,
// section O du FUSION : tous CONFIRMÉS). Aucun ne modifie un hurdle existant :
// ce sont des seuils d'entrée en charge, de sous-gates de contenu (SG, « le moins
// contraignant gagne ») ou de règles de modalité — jamais des gates de phase.
// `testIds` = lignes test_definitions qui portent la mesure (mapping 22/10/2026).
// ────────────────────────────────────────────────────────────────────────
export const FORCE_THRESHOLDS_V1_14 = [
  { ref: "G1", bloc: "Pré-op — entrée en charge lourde (« quiet knee »)", quadLsi: 70, statut: "BLOQUANT d'entrée au bloc pré-op chargé (pas un passage de phase)", testIds: ["preop→p1_quad_lsi"], source: "Eitzen 2010 / Delaware" },
  { ref: "G2", bloc: "EPIC usage tardif (6/9/12 mois)", quadEpic: 90, hopEpic: 90, statut: "CIBLE, conjointe avec le LSI (R4)", testIds: ["rtr_quad_epic", "preop→p1_hop_preop"], source: "Wellsandt 2017" },
  { ref: "G3", bloc: "Arrêt du NMES", quadLsi: 80, statut: "Règle de modalité (MVIC à 60°)", testIds: [], source: "UD2021 App. 5" },
  { ref: "G4", bloc: "SG1 — course terrain plat", quadLsi: 80, statut: "Sous-gate de contenu, non bloquant", testIds: ["p2→p3_isok_rtr", "p2→p3_quad_iso"], source: "UD2021 Transitional II" },
  { ref: "G5", bloc: "SG3 — agilités", quadLsi: 80, hopLsi: 80, statut: "Sous-gate de contenu, non bloquant", testIds: ["p3→p4_isok_rts", "p3→p4_single_hop_rts", "p3→p4_triple_hop_rts", "p3→p4_crossover_rts"], source: "UD2021 Transitional III" },
  { ref: "G6", bloc: "SG4 — plyométrie haute intensité", quadLsi: 85, hopLsi: 85, statut: "Sous-gate de contenu, non bloquant", testIds: ["p3→p4_isok_rts", "p3→p4_single_hop_rts", "p3→p4_triple_hop_rts", "p3→p4_crossover_rts"], source: "UD2021 Transitional III" },
  { ref: "G7", bloc: "SG5 — cutting / pivot", quadLsi: 90, hopLsi: 90, statut: "Sous-gate de contenu, non bloquant", testIds: ["p3→p4_isok_rts", "p3→p4_single_hop_rts", "p3→p4_triple_hop_rts", "p3→p4_crossover_rts"], source: "UD2021 Transitional IV" },
  { ref: "G8", bloc: "Sprint stade 1 — entrée", quadLsi: 80, statut: "Sous-gate de contenu, non bloquant", testIds: ["p3→p4_rts_sprint"], source: "UD2021 App. 8a / Lorenz 2020" },
  { ref: "G9", bloc: "Sprint stades 2 et 3 — entrée", quadLsi: 90, hopLsi: 90, statut: "Sous-gate de contenu, non bloquant", testIds: ["p3→p4_rts_sprint"], source: "UD2021 App. 8b/8c" },
];

// ────────────────────────────────────────────────────────────────────────
// V1.14 — CONSIGNE CHIRURGICALE « PLYOMÉTRIE LÉGÈRE » (Dr Joris, 16/10/2026, N4)
// Affichée dans le gate RTR-RUN. JAMAIS bloquante : n'entre ni dans les hurdles
// ni dans le score. Le kiné peut la moduler : override tracé (justification).
// ────────────────────────────────────────────────────────────────────────
export const PLYO_JORIS = {
  testId: "p2→p3_plyo_joris",
  label: "Consigne chirurgicale — plyométrie légère (~3 mois, DT4 et DIDT)",
  ouverture: "~3 mois post-op — plancher indicatif, la porte reste les critères",
  criteres: [
    "Genou sec (épanchement ≤ trace)",
    "Extension complète",
    "ROM ok",
    "Absence de douleur",
    "Bonne maîtrise du squat unipodal",
  ],
  cible: "LSI quadriceps ≥ 75 % — « idéalement » (cible, jamais bloquant)",
  statut: "CIBLE — jamais BLOQUANT",
  override: "Le kiné peut moduler selon l'évolution : override tracé, justification obligatoire",
  source: "[JOR2026] Dr Joris, communication écrite 16/10/2026 + décision J. Tubez",
  hors_perimetre: "Suture méniscale complexe / radiaire / racine : pas avant 4 mois après test isocinétique — nécessite le champ type_suture_meniscale (non encore en base).",
};

export const RTR_RULES = { version: RTR_VERSION, RUN: RTR_RUN, SPORT: RTR_SPORT, GIVING_WAY: GIVING_WAY_RULES, FORCE: FORCE_THRESHOLDS_V1_14, PLYO_JORIS };
export default RTR_RULES;
